import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plane, PlaneLanding, Route, RefreshCw, ShieldCheck, Receipt, BadgeCheck, Headphones, FileCheck2, Users } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { BookingWidget } from "@/components/BookingWidget";
import { api, inr } from "@/lib/api";

const SERVICES = [
  [PlaneLanding, "Airport Pickup", "Arrival pickups from Kempegowda International Airport with flight details captured at booking."],
  [Plane, "Airport Drop", "Area-group based fixed fares from across Bengaluru to the airport."],
  [Route, "Outstation One-Way", "Single-direction intercity trips. Fare confirmed by operations before dispatch."],
  [RefreshCw, "Outstation Round Trip", "Per-km pricing with a minimum of 300 km per day plus driver bata."],
];
const CATS = [
  ["Hatchback", "/images/hatchback.jpg", "Rates pending approval — not yet bookable."],
  ["Sedan", "/images/sedan.jpg", "Comfortable city and airport rides."],
  ["SUV", "/images/suv.jpg", "Ertiga, Crysta and Hycross class vehicles."],
];
const WHY = [
  [BadgeCheck, "Verified partners", "Every driver and vehicle passes document review — licence, PVC, RC, insurance and permit."],
  [Receipt, "Fares you can check", "Prices are calculated on our servers from a published, versioned rate card."],
  [ShieldCheck, "Locked-in booking price", "Your confirmed fare is saved with the booking and doesn't change when rates are updated."],
  [FileCheck2, "Automatic invoices", "A PDF invoice is generated when your trip is completed."],
  [Users, "Local partners", "We work with independent driver and vehicle partners. Nammaafleet does not own a fleet."],
  [Headphones, "Clear updates", "Driver and vehicle details are sent by email (and WhatsApp once enabled) after assignment."],
];
const FAQ = [
  ["When will I get my driver details?", "After our operations team assigns an eligible driver and vehicle. Booking confirmation alone does not mean a driver is assigned."],
  ["Are tolls and parking included?", "No. Toll, parking and interstate permit charges are extra and billed as per actuals where applicable."],
  ["Why does my fare say 'confirmation required'?", "Some routes (for example outstation one-way trips, or addresses matching more than one area group) need operations to confirm the fare before dispatch. We never guess a price."],
  ["Can I cancel?", "Yes, eligible bookings can be cancelled from your account. See the Cancellation & Refund Policy for timelines and charges."],
  ["Is Hatchback available?", "Not yet. Hatchback will become bookable once an approved rate is published."],
];

const Section = ({ id, eyebrow, title, children, dark }) => (
  <section id={id} className={`${dark ? "nf-navy text-white" : ""} py-20 lg:py-28`}>
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFB703]">{eyebrow}</p>
      <h2 className={`mt-3 max-w-2xl text-3xl font-semibold tracking-tight lg:text-4xl ${dark ? "text-white" : "text-[#0A192F]"}`}>{title}</h2>
      <div className="mt-12">{children}</div>
    </div>
  </section>
);

export default function Home() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/public/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const pk = cfg?.local?.packages;
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />
      <section className="relative overflow-hidden nf-navy nf-grain" data-testid="hero-section">
        <img src="/images/hero.jpg" alt="Bengaluru road at dusk" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/85 to-[#0A192F]/40" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-14 lg:grid-cols-12 lg:px-8 lg:py-20">
          <div className="nf-rise lg:col-span-6 lg:pt-10">
            <p className="font-kn text-lg text-[#FFB703]">ನಮ್ಮ ಫ್ಲೀಟ್ · Bengaluru</p>
            <h1 className="mt-4 text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
              Airport, outstation and local cabs — <span className="text-[#FFB703]">with clear, upfront fares.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-300">
              Book with verified driver partners. You'll see the fare breakdown before you confirm, and the price is locked in with your booking.
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-300">
              <span>✓ Published rate card {cfg && <b className="text-white">{cfg.rate_card_version}</b>}</span>
              <span>✓ Fare breakdown before you book</span>
            </div>
          </div>
          <div className="nf-rise lg:col-span-6" style={{ animationDelay: ".12s" }}><BookingWidget /></div>
        </div>
      </section>

      <Section id="services" eyebrow="Services" title="Airport transfers and outstation trips">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map(([I, t, d]) => (
            <div key={t} className="group rounded-2xl border border-slate-200 bg-white p-7 transition-transform hover:-translate-y-1 hover:shadow-md" data-testid={`service-card-${t.toLowerCase().replace(/\s/g, "-")}`}>
              <I className="h-8 w-8 text-[#0A192F] transition-colors group-hover:text-[#FFB703]" />
              <h3 className="mt-5 text-lg font-semibold text-[#0A192F]">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="packages" eyebrow="Local package duty" title="Hourly packages for city errands and meetings" dark>
        <div className="grid gap-6 md:grid-cols-2">
          {pk && Object.entries(pk).map(([k, p]) => (
            <div key={k} className="rounded-2xl border border-white/10 bg-white/5 p-8" data-testid={`package-card-${k}`}>
              <p className="text-3xl font-bold text-[#FFB703]">{p.hours} Hours / {p.km} KM</p>
              <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                {Object.entries(p.rates).map(([c, v]) => (
                  <div key={c} className="flex justify-between border-b border-white/10 py-2"><span className="text-slate-300">{c}</span><b>{inr(v)}</b></div>
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-400">Point limit: {Object.entries(p.points).map(([c, v]) => `${c} ${v ?? "to be confirmed"}`).join(" · ")}</p>
            </div>
          ))}
        </div>
        {cfg && <p className="mt-6 text-sm text-slate-300">Extra km: {Object.entries(cfg.local.extra_km).map(([c, v]) => `${c} ${inr(v)}`).join(" · ")} · Extra hour: {Object.entries(cfg.local.extra_hour).map(([c, v]) => `${c} ${inr(v)}`).join(" · ")}</p>}
      </Section>

      <Section id="vehicles" eyebrow="Vehicle categories" title="Choose the right category for your trip">
        <div className="grid gap-8 md:grid-cols-3">
          {CATS.map(([c, img, d]) => (
            <div key={c} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`category-card-${c.toLowerCase()}`}>
              <div className="aspect-[16/10] overflow-hidden bg-slate-100"><img src={img} alt={`${c} (illustrative)`} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" /></div>
              <div className="p-7">
                <div className="flex items-center justify-between"><h3 className="text-xl font-semibold text-[#0A192F]">{c}</h3>
                  {c === "Hatchback" && <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">Coming soon</span>}</div>
                <p className="mt-2 text-sm text-slate-600">{d}</p>
                <p className="mt-3 text-xs text-slate-400">Seats: {cfg?.category_capacity?.[c]?.passengers ?? "as per assigned vehicle"} · Illustrative image</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section id="fares" eyebrow="Transparent fares" title="Airport drop fares by area group">
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="airport-fare-table">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="p-4">Group</th><th className="p-4">Sample areas</th>{["Sedan", "Ertiga", "Crysta", "Hycross"].map((c) => <th key={c} className="p-4 text-right">{c}</th>)}</tr>
            </thead>
            <tbody>
              {cfg?.airport_groups?.map((g) => (
                <tr key={g.group} className="border-t">
                  <td className="p-4 font-bold text-[#0A192F]">{g.group}</td>
                  <td className="p-4 text-slate-600">{g.locations.slice(0, 5).join(", ")}{g.locations.length > 5 ? "…" : ""}{g.condition && <span className="block text-xs text-amber-700">{g.condition}</span>}</td>
                  {["Sedan", "Ertiga", "Crysta", "Hycross"].map((c) => <td key={c} className="p-4 text-right font-semibold">{inr(cfg.airport_drop_rates[String(g.group)]?.[c])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cfg && <p className="mt-4 text-sm text-slate-600">Outstation: {Object.entries(cfg.outstation.per_km).map(([c, v]) => `${c} ${inr(v)}/km`).join(" · ")} · Minimum {cfg.outstation.min_km_per_day} km/day · Driver bata extra per day. Toll, parking and permits are extra.</p>}
      </Section>

      <Section id="why" eyebrow="Why Nammaafleet" title="Built around verified partners and honest pricing">
        <div className="grid gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
          {WHY.map(([I, t, d]) => (
            <div key={t} className="flex gap-4"><I className="h-6 w-6 shrink-0 text-[#FFB703]" /><div><h3 className="font-semibold text-[#0A192F]">{t}</h3><p className="mt-1.5 text-sm leading-relaxed text-slate-600">{d}</p></div></div>
          ))}
        </div>
      </Section>

      <Section id="faq" eyebrow="FAQ" title="Frequently asked questions">
        <Accordion type="single" collapsible className="max-w-3xl" data-testid="faq-accordion">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={i} value={`q${i}`}><AccordionTrigger className="text-left text-base" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-slate-600">{a}</AccordionContent></AccordionItem>
          ))}
        </Accordion>
      </Section>

      <section className="pb-24">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 rounded-3xl bg-[#FFB703] px-8 py-12 md:flex-row md:items-center lg:px-14">
          <div><h2 className="text-2xl font-bold text-[#0A192F]">Own a cab? Partner with Nammaafleet.</h2><p className="mt-2 text-sm text-[#0A192F]/80">Register yourself and your vehicles in one application.</p></div>
          <div className="flex gap-3">
            <Link to="/partner" className="rounded-full bg-[#0A192F] px-6 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90" data-testid="cta-partner-btn">Become a partner</Link>
            <Link to="/contact" className="rounded-full border border-[#0A192F] px-6 py-3 text-sm font-bold text-[#0A192F]" data-testid="cta-contact-btn">Contact support</Link>
          </div>
        </div>
      </section>
      <SiteFooter cfg={cfg} />
    </div>
  );
}
